import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";import { MatchRing } from "@/components/MatchRing";
import { StatusBadge, STATUS_META, STATUS_ORDER } from "@/components/StatusBadge";
import { SkillsRadar } from "@/components/SkillsRadar";
import { BulkScoreDialog } from "@/components/BulkScoreDialog";
import { InvitesPanel } from "@/components/InvitesPanel";
import { DiversityPanel } from "@/components/DiversityPanel";
import { EditJobDialog } from "@/components/EditJobDialog";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, ChevronLeft, Mail, Download, Filter, Sparkles, EyeOff, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const JobApplicants = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [minScore, setMinScore] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [inviteOnly, setInviteOnly] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const canManage = user && (user.role === "admin" || user.role === "recruiter");

  const reloadJob = async () => {
    const r = await api.get(`/jobs/${id}`);
    setJob(r.data);
  };

  const deleteJob = async () => {
    if (!confirm(`Delete "${job?.title}"? This removes the job posting, all applications, and invites.`)) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job deleted");
      navigate(user?.role === "admin" ? "/admin" : "/recruiter");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  // Anonymised Review Mode — hides names, avatars, emails, and diversity tab.
  const [anon, setAnon] = useState(() => {
    try { return localStorage.getItem("axiony_anon_review") === "1"; } catch { return false; }
  });
  const toggleAnon = () => {
    const next = !anon;
    setAnon(next);
    try { localStorage.setItem("axiony_anon_review", next ? "1" : "0"); } catch {}
  };

  const load = () => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {});
    api.get(`/applications/job/${id}`).then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const updateStatus = async (application_id, next) => {
    try {
      await api.patch(`/applications/${application_id}/status`, { status: next });
      setApps((prev) => prev.map((a) => a.application_id === application_id ? { ...a, status: next } : a));
      if (selected?.application_id === application_id) setSelected({ ...selected, status: next });
      toast.success(`Marked as ${STATUS_META[next].label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const downloadResume = async (file_id, name) => {
    try {
      const res = await api.get(`/files/${file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "resume";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Resume unavailable"); }
  };

  // Statuses at which anonymised mode auto-reveals identity so screening prep is easy.
  const REVEAL_STATUSES = new Set(["shortlisted", "interviewed", "hired"]);
  const shouldMask = (a) => anon && !REVEAL_STATUSES.has(a.status || "submitted");

  const filtered = apps.filter((a) =>
    (a.match?.overall || 0) >= minScore
    && (statusFilter === "all" || a.status === statusFilter)
    && (!inviteOnly || a.via_invite)
  );
  const inviteCount = apps.filter((a) => a.via_invite).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Link to="/recruiter" className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label">applicants</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">{job?.title}</h1>
          <p className="text-slate-400 mt-1">{job?.company} · {job?.location}</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button
                onClick={() => setEditOpen(true)}
                variant="outline"
                data-testid="edit-job-btn"
                className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-full"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button
                onClick={deleteJob}
                variant="outline"
                data-testid="delete-job-btn"
                className="bg-transparent border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444] rounded-full"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
              </Button>
            </>
          )}
          <Button
            onClick={toggleAnon}
            variant="outline"
            data-testid="anon-toggle-btn"
            title="Hide names, avatars, emails, and diversity for blind first-pass review"
            className={`bg-transparent border-white/15 rounded-full font-medium ${
              anon
                ? "text-[#06B6D4] border-[#06B6D4]/50 bg-[#06B6D4]/5 hover:bg-[#06B6D4]/10"
                : "text-slate-100 hover:bg-white/5 hover:text-white"
            }`}
          >
            {anon ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" /> Anonymised: On
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" /> Anonymise
              </>
            )}
          </Button>
          <Button
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-score-open-btn"
            className="bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full font-medium"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Bulk score resumes
          </Button>
        </div>
      </div>

      <BulkScoreDialog
        jobId={id}
        jobTitle={job?.title || ""}
        open={bulkOpen}
        onOpenChange={setBulkOpen}
      />

      <EditJobDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        jobId={id}
        onSaved={reloadJob}
      />

      {/* Filters */}
      <div className="mt-8 border border-white/10 bg-[#111827] rounded-xl p-5 grid md:grid-cols-3 gap-6 items-center">
        <div>
          <div className="mono-label flex items-center gap-2 mb-3"><Filter className="w-3 h-3" /> minimum match score</div>
          <div className="flex items-center gap-4">
            <Slider
              value={[minScore]}
              onValueChange={(v) => setMinScore(v[0])}
              min={0} max={100} step={5}
              className="flex-1"
              data-testid="applicant-min-score-slider"
            />
            <span className="font-display text-xl w-10 text-right">{minScore}</span>
          </div>
        </div>
        <div>
          <div className="mono-label mb-3">status</div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="applicant-status-filter" className="bg-[#0B0F19] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10 text-slate-100">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="mono-label mb-3">showing</div>
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-2xl">{filtered.length} <span className="text-slate-500 text-base">/ {apps.length}</span></div>
            {inviteCount > 0 && (
              <button
                type="button"
                onClick={() => setInviteOnly(!inviteOnly)}
                data-testid="filter-invite-only"
                title="Show only candidates who came via a tracked invite"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-mono uppercase transition-colors ${
                  inviteOnly
                    ? "bg-[#06B6D4]/10 border-[#06B6D4]/50 text-[#06B6D4]"
                    : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                }`}
              >
                From invite · {inviteCount}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 mono-label text-slate-500">
          <div className="col-span-4">Candidate</div>
          <div className="col-span-2 text-center">Skills</div>
          <div className="col-span-2 text-center">Experience</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Overall</div>
        </div>
        {filtered.map((a, idx) => (
          <button
            key={a.application_id}
            onClick={() => setSelected(a)}
            data-testid={`applicant-row-${a.application_id}`}
            className={`w-full grid grid-cols-12 items-center px-5 py-4 text-left hover:bg-white/5 transition-colors ${idx !== filtered.length - 1 ? "border-b border-white/5" : ""}`}
          >
            <div className="col-span-4 flex items-center gap-3">
              <div className="text-xs text-slate-500 w-5 font-mono">{idx + 1}</div>
              <Avatar className="w-9 h-9 border border-white/10">
                {!shouldMask(a) && <AvatarImage src={a.candidate_picture} />}
                <AvatarFallback className="bg-[#1F2937] text-slate-300 text-xs">
                  {shouldMask(a)
                    ? `#${idx + 1}`
                    : a.candidate_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  {shouldMask(a) ? `Candidate #${idx + 1}` : a.candidate_name}
                  {a.via_invite && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded border border-[#06B6D4]/40 text-[#06B6D4] font-mono uppercase"
                      title="Applied via a tracked invite"
                    >
                      invite
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {shouldMask(a) ? (
                    <span className="font-mono uppercase tracking-wider text-[10px]">
                      identity hidden
                    </span>
                  ) : (
                    a.candidate_email
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-2 text-center"><ScorePill v={a.match.skills_match} /></div>
            <div className="col-span-2 text-center"><ScorePill v={a.match.experience_match} /></div>
            <div className="col-span-2"><StatusBadge status={a.status || "submitted"} testId={`applicant-status-${a.application_id}`} /></div>
            <div className="col-span-2 flex justify-end"><MatchRing value={a.match.overall} size={44} stroke={4} /></div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            {apps.length === 0 ? "No applications yet for this role." : "No applicants match the current filters."}
          </div>
        )}
      </div>

      {/* Invites + Diversity bottom section */}
      <div className="mt-8">
        <Tabs defaultValue="invites">
          <TabsList className="bg-[#0B0F19] border border-white/10">
            <TabsTrigger value="invites" data-testid="job-tab-invites">Invites</TabsTrigger>
            {!anon && (
              <TabsTrigger value="diversity" data-testid="job-tab-diversity">Diversity</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="invites" className="mt-4">
            <InvitesPanel jobId={id} />
          </TabsContent>
          {!anon && (
            <TabsContent value="diversity" className="mt-4">
              <DiversityPanel jobId={id} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4 justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-14 h-14 border border-white/10">
                      {!shouldMask(selected) && <AvatarImage src={selected.candidate_picture} />}
                      <AvatarFallback className="bg-[#1F2937] text-slate-300">
                        {shouldMask(selected)
                          ? "?"
                          : selected.candidate_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        {shouldMask(selected)
                          ? `Candidate #${filtered.findIndex((x) => x.application_id === selected.application_id) + 1}`
                          : selected.candidate_name}
                        {selected.via_invite && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#06B6D4]/40 text-[#06B6D4] font-mono uppercase">
                            invite
                          </span>
                        )}
                      </DialogTitle>
                      {shouldMask(selected) ? (
                        <button
                          type="button"
                          onClick={toggleAnon}
                          data-testid="modal-reveal-btn"
                          className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mt-1"
                        >
                          <Eye className="w-3.5 h-3.5" /> Reveal identity
                        </button>
                      ) : (
                        <a href={`mailto:${selected.candidate_email}`} className="text-sm text-[#06B6D4] hover:underline flex items-center gap-1 mt-1">
                          <Mail className="w-3.5 h-3.5" /> {selected.candidate_email}
                        </a>
                      )}
                      {anon && !shouldMask(selected) && (
                        <div className="text-[10px] text-slate-500 font-mono uppercase mt-1">
                          auto-revealed · {selected.status}
                        </div>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={selected.status || "submitted"} />
                </div>
              </DialogHeader>

              <div className="mt-4 flex items-center gap-6">
                <MatchRing value={selected.match.overall} size={90} stroke={7} />
                <div className="flex-1 space-y-2">
                  <Row label="Skills" v={selected.match.skills_match} />
                  <Row label="Experience" v={selected.match.experience_match} />
                  <Row label="Culture fit" v={selected.match.culture_fit} />
                </div>
              </div>

              {/* Status controls */}
              <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-white/10 pt-4">
                <span className="mono-label mr-2">move to</span>
                {STATUS_ORDER.filter((s) => s !== selected.status).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(selected.application_id, s)}
                    data-testid={`applicant-move-${s}`}
                    className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white h-7 rounded-full text-xs"
                  >
                    <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: STATUS_META[s].color }} />
                    {STATUS_META[s].label}
                  </Button>
                ))}
                {selected.resume_snapshot?.file_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadResume(selected.resume_snapshot.file_id, selected.resume_snapshot.original_filename)}
                    data-testid="applicant-download-resume"
                    className="ml-auto bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white h-7 rounded-full text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" /> Resume
                  </Button>
                )}
              </div>

              <Tabs defaultValue="summary" className="mt-6">
                <TabsList className="bg-[#0B0F19] border border-white/10">
                  <TabsTrigger value="summary" data-testid="applicant-tab-summary">AI Summary</TabsTrigger>
                  <TabsTrigger value="radar" data-testid="applicant-tab-radar">Skills Radar</TabsTrigger>
                  <TabsTrigger value="strengths" data-testid="applicant-tab-strengths">Strengths</TabsTrigger>
                  <TabsTrigger value="gaps" data-testid="applicant-tab-gaps">Gaps</TabsTrigger>
                  <TabsTrigger value="resume" data-testid="applicant-tab-resume">Snapshot</TabsTrigger>
                </TabsList>
                <TabsContent value="summary" className="text-sm text-slate-300 leading-relaxed mt-4">
                  {selected.match.summary}
                </TabsContent>
                <TabsContent value="radar" className="mt-4">
                  <SkillsRadar
                    jobId={id}
                    requiredSkills={job?.required_skills || []}
                    candidateSkills={selected.resume_snapshot?.skills || []}
                    resumeText={selected.resume_snapshot?.raw_text || ""}
                  />
                  <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500 font-mono uppercase justify-center">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-[#06B6D4]/60 border border-[#06B6D4]" />
                      Candidate coverage
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-slate-400/20 border border-slate-400/40" />
                      Required
                    </span>
                  </div>
                </TabsContent>
                <TabsContent value="strengths" className="mt-4">
                  <ul className="space-y-2">
                    {selected.match.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-slate-300 flex gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </TabsContent>
                <TabsContent value="gaps" className="mt-4">
                  <ul className="space-y-2">
                    {selected.match.gaps.map((s, i) => (
                      <li key={i} className="text-sm text-slate-300 flex gap-2">
                        <AlertCircle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </TabsContent>
                <TabsContent value="resume" className="mt-4 text-sm text-slate-300 space-y-2">
                  <div><span className="mono-label mr-2">headline</span>{selected.resume_snapshot?.headline || "—"}</div>
                  <div><span className="mono-label mr-2">years</span>{selected.resume_snapshot?.years_experience ?? "—"}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(selected.resume_snapshot?.skills || []).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded border border-white/10 text-slate-300">{s}</span>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ScorePill = ({ v }) => {
  const color = v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span style={{ color }}>{v}</span>
    </span>
  );
};

const Row = ({ label, v }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-200">{v}</span>
    </div>
    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444" }} />
    </div>
  </div>
);
